/**
 * S-20 Purchase result (V5 payments) — /store/success and /store/cancel.
 *
 * The success page is COSMETIC (EC-27): credit lands on the webhook whether
 * or not this page ever loads. It polls GET /store/checkout/:id and
 * celebrates when the webhook has credited; webhook lag > 5 s shows
 * ©store.pending and keeps polling — it never re-charges anything.
 *
 * v3: crediting is a BEAT — a gold Anton "GRANTED." stamp slams in over the
 * receipt card; pending is the mascot working, not a spinner. Reduced motion
 * collapses the stamp to a plain fade (stampVariants ride MotionConfig).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { copy, resolveCopy } from "@humble/shared";
import { api } from "../../lib/api.js";
import { Button, Card, Mascot, Skeleton, stampVariants } from "../../design/index.js";
import "./store.css";

type CheckoutState = "pending" | "credited" | "expired" | "refunded" | "missing";

export function StoreSuccessScreen() {
  const [params] = useSearchParams();
  const checkoutId = params.get("c");
  const [state, setState] = useState<CheckoutState | null>(null);
  const [slow, setSlow] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!checkoutId) {
      setState("missing");
      return;
    }
    let stopped = false;
    const poll = () => {
      api
        .get("/store/checkout/{checkoutId}", { params: { checkoutId } })
        .then((res) => {
          if (stopped) return;
          const s = ((res as { state?: string }).state ?? "pending") as CheckoutState;
          setState(s);
          if (s !== "pending" && timer.current) {
            clearInterval(timer.current);
            timer.current = null;
          }
        })
        .catch(() => {
          if (!stopped) setState("missing");
        });
    };
    poll();
    timer.current = setInterval(poll, 2000);
    const slowTimer = setTimeout(() => setSlow(true), 5000);
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
      clearTimeout(slowTimer);
    };
  }, [checkoutId]);

  return (
    <div className="store-result">
      <p className="h-caps store__kicker">Receipt</p>
      <h1 className="type-display-3">Purchase result</h1>
      <Card padding="lg" className="store-result__card" style={{ marginTop: 12 }}>
        {state === null ? (
          <Skeleton style={{ height: 120 }} />
        ) : state === "credited" ? (
          <>
            <motion.p
              className="type-display-2 neon-text store-result__stamp"
              variants={stampVariants}
              initial="initial"
              animate="enter"
            >
              {resolveCopy("store.granted_sting") ?? "GRANTED."}
            </motion.p>
            <p className="store-result__body" data-testid="result-success">
              {copy.store.success}
            </p>
          </>
        ) : state === "pending" ? (
          <>
            <Mascot mood="loading" />
            <p className="store-result__body" data-testid="result-pending" style={{ marginTop: 10 }}>
              {copy.store.pending}
              {slow ? " Still working — this page refreshes itself; your credit lands either way." : ""}
            </p>
          </>
        ) : state === "refunded" ? (
          <p className="store-result__body" data-testid="result-refunded">
            This payment was refunded in full — check the notice on your account. Nothing was lost.
          </p>
        ) : state === "expired" ? (
          <p className="store-result__body" data-testid="result-expired">
            {copy.store.canceled}
          </p>
        ) : (
          <p className="store-result__body">
            We could not find that checkout. If you paid, the credit still lands via webhook.
          </p>
        )}
        <div className="store-result__actions">
          <Link to="/store">
            <Button variant="ghost">Back to the store</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

export function StoreCancelScreen() {
  return (
    <div className="store-result">
      <p className="h-caps store__kicker">Receipt</p>
      <h1 className="type-display-3">Checkout canceled</h1>
      <Card padding="lg" className="store-result__card" style={{ marginTop: 12 }}>
        <Mascot mood="ko" />
        <p className="store-result__body" data-testid="result-canceled" style={{ marginTop: 10 }}>
          {copy.store.canceled}
        </p>
        <div className="store-result__actions">
          <Link to="/store">
            <Button variant="ghost">Back to the store</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
